import { CSSProperties } from "react";
import IconClaude from "@/app/(components)/generics/Icons/IconClaude";
import IconGmail from "@/app/(components)/generics/Icons/IconGmail";
import IconGoogleDrive from "@/app/(components)/generics/Icons/IconGoogleDrive";
import IconGoogleMeet from "@/app/(components)/generics/Icons/IconGoogleMeet";
import IconHubSpot from "@/app/(components)/generics/Icons/IconHubSpot";
import IconLinkedInBrand from "@/app/(components)/generics/Icons/IconLinkedInBrand";
import IconNotion from "@/app/(components)/generics/Icons/IconNotion";
import IconOpenAI from "@/app/(components)/generics/Icons/IconOpenAI";
import IconPlug from "@/app/(components)/generics/Icons/IconPlug";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import IconSalesforce from "@/app/(components)/generics/Icons/IconSalesforce";
import IconSparkles from "@/app/(components)/generics/Icons/IconSparkles";
import IconSync from "@/app/(components)/generics/Icons/IconSync";
import IconTeamsBrand from "@/app/(components)/generics/Icons/IconTeamsBrand";
import IconWhatsAppBrand from "@/app/(components)/generics/Icons/IconWhatsAppBrand";
import IconZoomBrand from "@/app/(components)/generics/Icons/IconZoomBrand";
import {
  buildLinkPath,
  CONCEPT_FLOW_VIRTUAL_WIDTH,
} from "@/app/(components)/generics/Illustrations/IlluConceptFlowLinks/IlluConceptFlowLinks.logic";
import { TKey } from "@/app/translations/TKey";
import {
  ConceptFlowArrival,
  ConceptFlowDeckChip,
  ConceptFlowGutter,
  ConceptFlowMessage,
  ConceptFlowOutput,
  ConceptFlowParticipant,
  ConceptFlowRoute,
  ConceptFlowSource,
  ConceptFlowStep,
  ConceptFlowSvgPacket,
  ConceptFlowTagPair,
  ConceptFlowTagPairView,
  ConceptFlowWaveBar,
} from "@/app/types/home/ConceptFlow";
import {
  ConceptFlowLinkPath,
  ConceptFlowLinkPoint,
} from "@/app/types/illustrations/ConceptFlowLink";

export const LANDING_IMAGES_DIR = "/assets/images/landing/";

const FLOW_CYCLE_SECONDS = 18;
const FLOW_SLOT_SECONDS = 4.5;
const FLOW_TRAVEL_SECONDS = 2.25;
const FLOW_HANDOFF_SECONDS = 0.3;
const FLOW_SOURCES_LINK_INDEX = 2;
const FLOW_SOURCES_WAVE_LEAD_SECONDS = 0.9;
const FLOW_SOURCES_WAVE_STEP_SECONDS = 0.08;
const FLOW_WAVE_BAR_STEP_SECONDS = 0.13;
const FLOW_TAG_MIDPOINT_PROGRESS = 0.5;
const FLOW_CONTENT_HEIGHT = 600;
const FLOW_LEFT_ROWS = [260, 280, 60];
const FLOW_RIGHT_ROWS = [160, 160, 160];
const FLOW_PACKET_TRAVEL_SHARE = 0.125;
const FLOW_TAG_DECK_SWITCH_SECONDS = 1;
const FLOW_SEQUENCE_STEP_SECONDS = 0.45;
const FLOW_MORE_SOURCES = "+20";

function toWaveBars(heights: number[]): ConceptFlowWaveBar[] {
  return heights.map((height, index) => ({ id: `bar${index}`, height }));
}

function toSeconds(value: number): string {
  return `${Number(value.toFixed(2))}s`;
}

function delayStyle(seconds: number): CSSProperties {
  return { "--flow-delay": toSeconds(seconds) } as CSSProperties;
}

function slotStart(slot: number): number {
  return slot * FLOW_SLOT_SECONDS;
}

function outputDeparture(slot: number): number {
  return slotStart(slot) + FLOW_TRAVEL_SECONDS + FLOW_HANDOFF_SECONDS;
}

function outputArrival(slot: number): number {
  return outputDeparture(slot) + FLOW_TRAVEL_SECONDS;
}

export const FLOW_TIMELINE_STYLE = {
  "--flow-cycle": toSeconds(FLOW_CYCLE_SECONDS),
  "--flow-slot": toSeconds(FLOW_SLOT_SECONDS),
  "--flow-arrival": toSeconds(FLOW_TRAVEL_SECONDS),
} as CSSProperties;

export const FLOW_STEPS: ConceptFlowStep[] = [
  { step: 1, nameKey: TKey.HOME_MEIDLY_FLOW_STEP_1, textKey: TKey.HOME_MEIDLY_FLOW_STEP_1_TEXT },
  { step: 2, nameKey: TKey.HOME_MEIDLY_FLOW_STEP_2, textKey: TKey.HOME_MEIDLY_FLOW_STEP_2_TEXT },
  { step: 3, nameKey: TKey.HOME_MEIDLY_FLOW_STEP_3, textKey: TKey.HOME_MEIDLY_FLOW_STEP_3_TEXT },
];

export const [FLOW_STEP_CAPTURE, FLOW_STEP_UNDERSTAND, FLOW_STEP_ACTIVATE] = FLOW_STEPS;

export const FLOW_CALL_TIMER = "05:42";

export const FLOW_CONTACT_PHOTO = `${LANDING_IMAGES_DIR}contact-1.jpg`;

export const FLOW_AVATAR_SIZE = 96;

export const FLOW_MESSAGES: ConceptFlowMessage[] = [
  {
    textKey: TKey.HOME_MEIDLY_FLOW_MESSAGE_IN,
    photo: FLOW_CONTACT_PHOTO,
    time: "10:41",
    isOutgoing: false,
  },
  {
    textKey: TKey.HOME_MEIDLY_FLOW_MESSAGE_OUT,
    photo: `${LANDING_IMAGES_DIR}contact-2.jpg`,
    time: "10:42",
    isOutgoing: true,
  },
];

export const FLOW_PARTICIPANT_PHOTO_WIDTH = 336;

export const FLOW_PARTICIPANT_PHOTO_HEIGHT = 150;

export const FLOW_PARTICIPANTS: ConceptFlowParticipant[] = [
  { photo: `${LANDING_IMAGES_DIR}participant-1.jpg`, isSpeaking: true },
  { photo: `${LANDING_IMAGES_DIR}participant-2.jpg`, isSpeaking: false },
  { photo: `${LANDING_IMAGES_DIR}participant-3.jpg`, isSpeaking: false },
  { photo: `${LANDING_IMAGES_DIR}participant-4.jpg`, isSpeaking: false },
];

export const FLOW_CALL_WAVE: ConceptFlowWaveBar[] = toWaveBars([
  30, 60, 45, 80, 50, 70, 35, 55, 25,
]);

export function waveBarStyle(bar: ConceptFlowWaveBar, index: number): CSSProperties {
  return {
    height: `${bar.height}%`,
    animationDelay: toSeconds(-index * FLOW_WAVE_BAR_STEP_SECONDS),
  };
}

export const FLOW_SOURCES: ConceptFlowSource[] = [
  { labelKey: TKey.HOME_MEIDLY_FLOW_SOURCE_MEET, Icon: IconGoogleMeet },
  { labelKey: TKey.HOME_MEIDLY_FLOW_SOURCE_ZOOM, Icon: IconZoomBrand },
  { labelKey: TKey.HOME_MEIDLY_FLOW_SOURCE_TEAMS, Icon: IconTeamsBrand },
  { labelKey: TKey.HOME_MEIDLY_FLOW_SOURCE_DRIVE, Icon: IconGoogleDrive },
  { labelKey: TKey.HOME_MEIDLY_FLOW_SOURCE_EMAIL, Icon: IconGmail },
  { labelKey: TKey.HOME_MEIDLY_FLOW_SOURCE_LINKEDIN, Icon: IconLinkedInBrand },
  { labelKey: TKey.HOME_MEIDLY_FLOW_SOURCE_WHATSAPP, Icon: IconWhatsAppBrand },
  { labelKey: TKey.HOME_MEIDLY_FLOW_SOURCE_MORE, text: FLOW_MORE_SOURCES },
];

const FLOW_TAG_PAIRS: ConceptFlowTagPair[] = [
  {
    chips: [
      { labelKey: TKey.EXCHANGE_TAG_OBJECTION, toneClass: "toneObjection" },
      { labelKey: TKey.EXCHANGE_TAG_PAIN_POINT, toneClass: "tonePain" },
      { labelKey: TKey.EXCHANGE_TAG_TIMELINE, toneClass: "toneTrends" },
    ],
    isOutgoing: false,
    linkIndex: 0,
  },
  {
    chips: [
      { labelKey: TKey.EXCHANGE_TAG_BUYING_SIGNAL, toneClass: "toneSignal" },
      { labelKey: TKey.EXCHANGE_TAG_BUDGET, toneClass: "toneObjection" },
      { labelKey: TKey.EXCHANGE_TAG_POSITIVE_FEEDBACK, toneClass: "toneSpeech" },
    ],
    isOutgoing: false,
    linkIndex: 2,
  },
  {
    chips: [
      { labelKey: TKey.EXCHANGE_TAG_BUYING_REASON, toneClass: "toneSignal" },
      { labelKey: TKey.EXCHANGE_TAG_DECISION_CRITERIA, toneClass: "toneBalance" },
      { labelKey: TKey.EXCHANGE_TAG_NEXT_STEP, toneClass: "toneObjection" },
    ],
    isOutgoing: true,
    linkIndex: 0,
  },
  {
    chips: [
      { labelKey: TKey.EXCHANGE_TAG_COMPETITOR_MENTION, toneClass: "toneTrends" },
      { labelKey: TKey.EXCHANGE_TAG_CHURN_RISK, toneClass: "tonePain" },
      { labelKey: TKey.EXCHANGE_TAG_CUSTOMER_QUESTION, toneClass: "toneSpeech" },
    ],
    isOutgoing: true,
    linkIndex: 2,
  },
];

export const FLOW_OUTPUTS: ConceptFlowOutput[] = [
  {
    Icon: IconSync,
    titleKey: TKey.HOME_MEIDLY_FLOW_RECORD_TITLE,
    noteKey: TKey.HOME_MEIDLY_FLOW_RECORD_NOTE,
    syncKey: TKey.HOME_MEIDLY_FLOW_RECORD_SYNC,
    syncLogos: [
      { id: "hubspot", Icon: IconHubSpot },
      { id: "salesforce", Icon: IconSalesforce },
      { id: "notion", Icon: IconNotion },
    ],
  },
  {
    Icon: IconSparkles,
    titleKey: TKey.HOME_MEIDLY_FLOW_ANSWER_TITLE,
    noteKey: TKey.HOME_MEIDLY_FLOW_ANSWER_NOTE,
    avatarStack: {
      photos: [
        `${LANDING_IMAGES_DIR}contact-1.jpg`,
        `${LANDING_IMAGES_DIR}contact-3.jpg`,
        `${LANDING_IMAGES_DIR}contact-2.jpg`,
      ],
      countKey: TKey.HOME_MEIDLY_FLOW_ANSWER_COUNT,
    },
  },
  {
    Icon: IconPlug,
    titleKey: TKey.HOME_MEIDLY_FLOW_MCP_TITLE,
    noteKey: TKey.HOME_MEIDLY_FLOW_MCP_NOTE,
    syncKey: TKey.HOME_MEIDLY_FLOW_MCP_SYNC,
    syncLogos: [
      { id: "claude", Icon: IconClaude, toneClass: "logoClaude" },
      { id: "openai", Icon: IconOpenAI, toneClass: "logoOpenAI" },
      { id: "more", Icon: IconPlus, toneClass: "logoMore" },
    ],
  },
];

const FLOW_ROUTES: ConceptFlowRoute[] = [
  { inLinkIndex: 0, outputIndex: 0 },
  { inLinkIndex: 1, outputIndex: 1 },
  { inLinkIndex: FLOW_SOURCES_LINK_INDEX, outputIndex: 2 },
  { inLinkIndex: 1, outputIndex: 1 },
];

function rowCenters(rows: number[]): number[] {
  const total = rows.reduce((sum, row) => sum + row, 0);
  let cursor = (FLOW_CONTENT_HEIGHT - total) / 2;
  return rows.map((row) => {
    const center = cursor + row / 2;
    cursor += row;
    return center;
  });
}

const FLOW_HUB_Y = FLOW_CONTENT_HEIGHT / 2;
const FLOW_IN_ANCHORS = rowCenters(FLOW_LEFT_ROWS);
const FLOW_OUT_ANCHORS = rowCenters(FLOW_RIGHT_ROWS);

function toPixels(values: number[]): string {
  return values.map((value) => `${value}px`).join(" ");
}

export const FLOW_LAYOUT_STYLE = {
  "--flow-content-height": `${FLOW_CONTENT_HEIGHT}px`,
  "--flow-left-rows": toPixels(FLOW_LEFT_ROWS),
  "--flow-right-rows": toPixels(FLOW_RIGHT_ROWS),
} as CSSProperties;

export const FLOW_GUTTER_HEIGHT = FLOW_CONTENT_HEIGHT;

function gutterEnds(
  isOutgoing: boolean,
  anchorY: number,
): [ConceptFlowLinkPoint, ConceptFlowLinkPoint] {
  const edge = { x: 0, y: isOutgoing ? FLOW_HUB_Y : anchorY };
  const far = { x: CONCEPT_FLOW_VIRTUAL_WIDTH, y: isOutgoing ? anchorY : FLOW_HUB_Y };
  return [edge, far];
}

function gutterLinks(isOutgoing: boolean): ConceptFlowLinkPath[] {
  const anchors = isOutgoing ? FLOW_OUT_ANCHORS : FLOW_IN_ANCHORS;
  return anchors.map((anchorY, index) => {
    const [from, to] = gutterEnds(isOutgoing, anchorY);
    return { id: `${isOutgoing ? "out" : "in"}${index}`, d: buildLinkPath(from, to) };
  });
}

function gutterPackets(isOutgoing: boolean, links: ConceptFlowLinkPath[]): ConceptFlowSvgPacket[] {
  return FLOW_ROUTES.flatMap((route, slot) => {
    const link = links[isOutgoing ? route.outputIndex : route.inLinkIndex];
    if (!link) {
      return [];
    }
    const begin = isOutgoing ? outputDeparture(slot) : slotStart(slot);
    return [{ id: `${link.id}slot${slot}`, d: link.d, begin: toSeconds(begin) }];
  });
}

export const FLOW_PACKET_DURATION = toSeconds(FLOW_CYCLE_SECONDS);

export const FLOW_PACKET_KEY_TIMES = `0;${FLOW_PACKET_TRAVEL_SHARE};1`;

export const FLOW_PACKET_KEY_POINTS = "0;1;1";

export const FLOW_PACKET_KEY_SPLINES = "0.42 0 0.58 1;0 0 1 1";

export const FLOW_PACKET_FADE_VALUES = "0;0.7;0.7;0;0";

export const FLOW_PACKET_FADE_TIMES = "0;0.02;0.11;0.13;1";

export const FLOW_PACKET_SHAPE = "M0 0 L0 0.01";

function tagPassSeconds(pair: ConceptFlowTagPair): number {
  const slot = Math.max(
    FLOW_ROUTES.findIndex((route) =>
      pair.isOutgoing ? route.outputIndex === pair.linkIndex : route.inLinkIndex === pair.linkIndex,
    ),
    0,
  );
  const departure = pair.isOutgoing ? outputDeparture(slot) : slotStart(slot);
  return departure + FLOW_TRAVEL_SECONDS * FLOW_TAG_MIDPOINT_PROGRESS;
}

function tagDeck(pair: ConceptFlowTagPair): ConceptFlowDeckChip[] {
  const labelSeconds = FLOW_CYCLE_SECONDS / pair.chips.length;
  const frontSince = tagPassSeconds(pair) - FLOW_TAG_DECK_SWITCH_SECONDS;
  return pair.chips.map((chip, index) => ({
    ...chip,
    style: {
      "--deck-delay": toSeconds(frontSince + index * labelSeconds - FLOW_CYCLE_SECONDS),
    } as CSSProperties,
  }));
}

function tagPairView(pair: ConceptFlowTagPair, style: CSSProperties): ConceptFlowTagPairView {
  return {
    id: pair.chips.map((chip) => chip.labelKey).join(),
    deck: tagDeck(pair),
    style: { ...delayStyle(tagPassSeconds(pair)), ...style },
  };
}

export const FLOW_LIST_TAG_PAIRS: ConceptFlowTagPairView[] = FLOW_TAG_PAIRS.map((pair) =>
  tagPairView(pair, {}),
);

function gutterTags(isOutgoing: boolean): ConceptFlowTagPairView[] {
  const anchors = isOutgoing ? FLOW_OUT_ANCHORS : FLOW_IN_ANCHORS;
  return FLOW_TAG_PAIRS.filter((pair) => pair.isOutgoing === isOutgoing).flatMap((pair) => {
    const anchorY = anchors[pair.linkIndex];
    if (anchorY === undefined) {
      return [];
    }
    return [tagPairView(pair, { left: "50%", top: `${Math.round((anchorY + FLOW_HUB_Y) / 2)}px` })];
  });
}

function buildGutter(isOutgoing: boolean): ConceptFlowGutter {
  const links = gutterLinks(isOutgoing);
  return {
    id: isOutgoing ? "gutterOut" : "gutterIn",
    isOutgoing,
    links,
    packets: gutterPackets(isOutgoing, links),
    tags: gutterTags(isOutgoing),
  };
}

export const FLOW_GUTTERS: ConceptFlowGutter[] = [buildGutter(false), buildGutter(true)];

export function outputArrivals(outputIndex: number): ConceptFlowArrival[] {
  return FLOW_ROUTES.flatMap((route, slot) =>
    route.outputIndex === outputIndex
      ? [
          {
            id: `arrival${slot}`,
            style: delayStyle(outputArrival(slot)),
          },
        ]
      : [],
  );
}

export function outputSequenceStyle(outputIndex: number, itemIndex: number): CSSProperties {
  const slot = FLOW_ROUTES.findIndex((route) => route.outputIndex === outputIndex);
  return delayStyle(outputArrival(Math.max(slot, 0)) + itemIndex * FLOW_SEQUENCE_STEP_SECONDS);
}

export function sourceWaveStyle(sourceIndex: number): CSSProperties {
  const slot = FLOW_ROUTES.findIndex((route) => route.inLinkIndex === FLOW_SOURCES_LINK_INDEX);
  const start = slotStart(Math.max(slot, 0)) - FLOW_SOURCES_WAVE_LEAD_SECONDS;
  const delay = start + sourceIndex * FLOW_SOURCES_WAVE_STEP_SECONDS + FLOW_CYCLE_SECONDS;
  return delayStyle(delay % FLOW_CYCLE_SECONDS);
}

export function formatStepNumber(step: number): string {
  return String(step).padStart(2, "0");
}
